import type {
  DocumentEngineeringTool,
  DocumentMarkupPoint,
  DocumentMarkupShapeTool,
  DocumentMarkupStroke,
  DocumentMarkupTool,
} from '../../types';

export const ENG_STROKE = '#c41e3a';
export const ENG_CALLOUT_FILL = '#c8f4f4';
export const ENGINEERING_TOOLS: readonly DocumentEngineeringTool[] = ['callout', 'line', 'zigzag', 'stamp'];
export const SHAPE_MARKUP_TOOLS: readonly DocumentMarkupShapeTool[] = ['cloud', 'typewriter'];

export function isEngineeringMarkupTool(tool: DocumentMarkupTool | string): tool is DocumentEngineeringTool {
  return (ENGINEERING_TOOLS as readonly string[]).includes(tool);
}

export function isShapeMarkupTool(tool: DocumentMarkupTool | string): tool is DocumentMarkupShapeTool {
  return (SHAPE_MARKUP_TOOLS as readonly string[]).includes(tool);
}

export function isStoredFreehandTool(tool: DocumentMarkupTool | string): tool is 'pen' | 'highlighter' {
  return tool === 'pen' || tool === 'highlighter';
}

/** Pen / highlighter / line keep drawing; selection is click-to-edit, not post-commit. */
export function isContinuousDrawTool(tool: DocumentMarkupTool | string): boolean {
  return tool === 'pen' || tool === 'highlighter' || tool === 'line';
}

/** Click vs drag: a tap should select a mark instead of committing a speck. */
export function isMarkupTapGesture(
  points: DocumentMarkupPoint[] | null | undefined,
  start?: DocumentMarkupPoint | null,
  maxDist = 0.8,
): boolean {
  if (!points || points.length === 0) return true;
  const first = start || points[0];
  const last = points[points.length - 1];
  if (dist(first, last) >= maxDist) return false;
  if (points.length <= 2) return true;
  let travel = 0;
  for (let i = 1; i < points.length; i++) travel += dist(points[i - 1], points[i]);
  return travel < maxDist * 1.5;
}

export function dist(a: DocumentMarkupPoint, b: DocumentMarkupPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Pins the live cursor onto a line/polyline, skipping a duplicate of the last vertex. */
export function finishLinePoints(
  pinned: DocumentMarkupPoint[],
  live?: DocumentMarkupPoint | null,
  minDist = 0.8
): DocumentMarkupPoint[] | null {
  const pts = pinned.slice();
  if (live) {
    const last = pts[pts.length - 1];
    if (!last || dist(last, live) >= minDist) pts.push(live);
  }
  if (pts.length < 2) return null;
  if (pts.length === 2 && dist(pts[0], pts[1]) < minDist) return null;
  return pts;
}

export function distToSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number
): number {
  const abx = bx - ax;
  const aby = by - ay;
  const apx = px - ax;
  const apy = py - ay;
  const ab2 = abx * abx + aby * aby;
  if (ab2 < 1e-9) return Math.hypot(apx, apy);
  let t = (apx * abx + apy * aby) / ab2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * abx), py - (ay + t * aby));
}

export function distToPolyline(px: number, py: number, points: DocumentMarkupPoint[]): number {
  if (points.length === 0) return Infinity;
  if (points.length === 1) return Math.hypot(px - points[0].x, py - points[0].y);
  let min = Infinity;
  for (let i = 1; i < points.length; i++) {
    const d = distToSegment(px, py, points[i - 1].x, points[i - 1].y, points[i].x, points[i].y);
    if (d < min) min = d;
  }
  return min;
}

/** Orthogonal elbow: travel the dominant axis first (CAD-style bent leader). */
export function orthogonalElbow(a: DocumentMarkupPoint, b: DocumentMarkupPoint): DocumentMarkupPoint {
  const dx = Math.abs(b.x - a.x);
  const dy = Math.abs(b.y - a.y);
  return dx >= dy ? { x: b.x, y: a.y } : { x: a.x, y: b.y };
}

export function wrapLabel(text: string, max = 32): string[] {
  const raw = (text || '').split('\n');
  const lines: string[] = [];
  for (const line of raw) {
    if (line.length <= max) {
      lines.push(line);
      continue;
    }
    let cur = '';
    for (const word of line.split(/\s+/)) {
      const next = cur ? `${cur} ${word}` : word;
      if (next.length > max && cur) {
        lines.push(cur);
        cur = word;
      } else {
        cur = next;
      }
    }
    if (cur) lines.push(cur);
  }
  return lines.length ? lines : [''];
}

export function calloutBoxPx(text: string): { w: number; h: number } {
  const lines = wrapLabel(text || 'Note');
  const maxChars = Math.max(...lines.map(l => l.length), 6);
  return {
    w: Math.min(300, Math.max(78, maxChars * 7.6 + 18)),
    h: Math.max(28, lines.length * 17 + 14),
  };
}

/**
 * Zigzag (break / emphasis) along A→B in page space.
 * `amplitude` and `tooth` are in the same 0–100 units.
 */
export function zigzagPoints(
  a: DocumentMarkupPoint,
  b: DocumentMarkupPoint,
  amplitude = 1.15,
  tooth = 2.2
): DocumentMarkupPoint[] {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len < 0.15) return [a, b];
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy;
  const py = ux;
  const teeth = Math.max(3, Math.round(len / tooth));
  const pts: DocumentMarkupPoint[] = [a];
  for (let i = 1; i < teeth; i++) {
    const t = i / teeth;
    const side = i % 2 === 0 ? 1 : -1;
    pts.push({
      x: a.x + ux * len * t + px * amplitude * side,
      y: a.y + uy * len * t + py * amplitude * side,
    });
  }
  pts.push(b);
  return pts;
}

export function pointsToPath(pts: DocumentMarkupPoint[]): string {
  return pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
}

export function strokePolyline(stroke: DocumentMarkupStroke): DocumentMarkupPoint[] {
  if (stroke.tool === 'zigzag' && stroke.points.length >= 2) {
    return zigzagPoints(stroke.points[0], stroke.points[stroke.points.length - 1]);
  }
  return stroke.points;
}

export const STAMP_BASE_PX = 34;
export const STAMP_LABEL_PX = 14;
export const STAMP_ROTATE_GAP_PX = 20;

export function stampHalfPx(stampScale: number): number {
  return (STAMP_BASE_PX * Math.max(0.25, stampScale) + STAMP_LABEL_PX) / 2;
}

export function stampHitRadius(svgW: number, svgH: number, stampScale: number): number {
  const px = stampHalfPx(stampScale);
  return Math.max((px * 100) / Math.max(svgW, 1), (px * 100) / Math.max(svgH, 1), 1.6);
}

export type StampHandle = 'nw' | 'ne' | 'se' | 'sw' | 'rotate' | 'body';

export function pageToPx(p: DocumentMarkupPoint, svgW: number, svgH: number): { x: number; y: number } {
  return { x: (p.x / 100) * svgW, y: (p.y / 100) * svgH };
}

export function pageToStampLocal(
  p: DocumentMarkupPoint,
  center: DocumentMarkupPoint,
  rotationDeg: number,
  svgW: number,
  svgH: number
): { x: number; y: number } {
  const sx = 100 / Math.max(svgW, 1);
  const sy = 100 / Math.max(svgH, 1);
  const dx = (p.x - center.x) / sx;
  const dy = (p.y - center.y) / sy;
  const rad = (-rotationDeg * Math.PI) / 180;
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  return { x: dx * c - dy * s, y: dx * s + dy * c };
}

export function hitStampHandle(
  p: DocumentMarkupPoint,
  stroke: DocumentMarkupStroke,
  svgW: number,
  svgH: number
): StampHandle | null {
  const center = stroke.points[0];
  if (!center) return null;
  const half = stampHalfPx(stroke.stampScale ?? 1);
  const local = pageToStampLocal(p, center, stroke.stampRotation ?? 0, svgW, svgH);
  const handleR = 11;
  const corners: { id: Exclude<StampHandle, 'rotate' | 'body'>; x: number; y: number }[] = [
    { id: 'nw', x: -half, y: -half },
    { id: 'ne', x: half, y: -half },
    { id: 'se', x: half, y: half },
    { id: 'sw', x: -half, y: half },
  ];
  for (const corner of corners) {
    if (Math.hypot(local.x - corner.x, local.y - corner.y) <= handleR) return corner.id;
  }
  if (Math.hypot(local.x, local.y + half + STAMP_ROTATE_GAP_PX) <= handleR) return 'rotate';
  if (Math.abs(local.x) <= half && Math.abs(local.y) <= half) return 'body';
  return null;
}

export function pxAngle(from: DocumentMarkupPoint, to: DocumentMarkupPoint, svgW: number, svgH: number): number {
  const a = pageToPx(from, svgW, svgH);
  const b = pageToPx(to, svgW, svgH);
  return (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
}

export function pxDist(a: DocumentMarkupPoint, b: DocumentMarkupPoint, svgW: number, svgH: number): number {
  const pa = pageToPx(a, svgW, svgH);
  const pb = pageToPx(b, svgW, svgH);
  return Math.hypot(pb.x - pa.x, pb.y - pa.y);
}

export function calloutHitsPoint(
  p: DocumentMarkupPoint,
  stroke: DocumentMarkupStroke,
  svgW: number,
  svgH: number
): boolean {
  const pts = stroke.points;
  if (!pts.length) return false;
  const pin = pts[0];
  const box = pts[pts.length - 1];
  if (dist(p, pin) <= 2.2) return true;
  if (distToPolyline(p.x, p.y, pts) <= 2) return true;
  const { w, h } = calloutBoxPx(stroke.text || '');
  const bw = (w * 100) / Math.max(svgW, 1);
  const bh = (h * 100) / Math.max(svgH, 1);
  return Math.abs(p.x - box.x) <= bw / 2 && Math.abs(p.y - box.y) <= bh / 2;
}

export function typewriterHitsPoint(
  p: DocumentMarkupPoint,
  stroke: DocumentMarkupStroke,
  svgW: number,
  svgH: number
): boolean {
  const box = stroke.points[0];
  if (!box) return false;
  const { w, h } = calloutBoxPx(stroke.text || 'Note');
  const bw = (w * 100) / Math.max(svgW, 1);
  const bh = (h * 100) / Math.max(svgH, 1);
  return Math.abs(p.x - box.x) <= bw / 2 && Math.abs(p.y - box.y) <= bh / 2;
}

export function cloudRect(a: DocumentMarkupPoint, b: DocumentMarkupPoint): {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  w: number;
  h: number;
} {
  const x0 = Math.min(a.x, b.x);
  const y0 = Math.min(a.y, b.y);
  const x1 = Math.max(a.x, b.x);
  const y1 = Math.max(a.y, b.y);
  return { x0, y0, x1, y1, w: Math.max(0.5, x1 - x0), h: Math.max(0.5, y1 - y0) };
}

/** Scalloped revision-cloud path from two opposite corners in 0–100 page space. */
export function revisionCloudPath(a: DocumentMarkupPoint, b: DocumentMarkupPoint): string {
  const { x0, y0, x1, y1, w, h } = cloudRect(a, b);
  const bump = Math.max(0.6, Math.min(2.4, Math.min(w, h) / 6));
  const segsX = Math.max(3, Math.round(w / (bump * 1.7)));
  const segsY = Math.max(2, Math.round(h / (bump * 1.7)));
  const parts: string[] = [`M ${x0} ${y0}`];
  for (let i = 0; i < segsX; i++) {
    const t0 = i / segsX;
    const t1 = (i + 1) / segsX;
    parts.push(`Q ${x0 + ((t0 + t1) / 2) * w} ${y0 - bump} ${x0 + t1 * w} ${y0}`);
  }
  for (let i = 0; i < segsY; i++) {
    const t0 = i / segsY;
    const t1 = (i + 1) / segsY;
    parts.push(`Q ${x1 + bump} ${y0 + ((t0 + t1) / 2) * h} ${x1} ${y0 + t1 * h}`);
  }
  for (let i = 0; i < segsX; i++) {
    const t0 = i / segsX;
    const t1 = (i + 1) / segsX;
    parts.push(`Q ${x1 - ((t0 + t1) / 2) * w} ${y1 + bump} ${x1 - t1 * w} ${y1}`);
  }
  for (let i = 0; i < segsY; i++) {
    const t0 = i / segsY;
    const t1 = (i + 1) / segsY;
    parts.push(`Q ${x0 - bump} ${y1 - ((t0 + t1) / 2) * h} ${x0} ${y1 - t1 * h}`);
  }
  parts.push('Z');
  return parts.join(' ');
}

export function cloudHitsPoint(p: DocumentMarkupPoint, stroke: DocumentMarkupStroke): boolean {
  if (stroke.points.length < 2) return false;
  const { x0, y0, x1, y1, w, h } = cloudRect(stroke.points[0], stroke.points[1]);
  const pad = Math.max(1.2, Math.min(2.4, Math.min(w, h) / 6));
  return p.x >= x0 - pad && p.x <= x1 + pad && p.y >= y0 - pad && p.y <= y1 + pad;
}

export function markupHitsPoint(
  p: DocumentMarkupPoint,
  stroke: DocumentMarkupStroke,
  svgW: number,
  svgH: number
): number {
  if (stroke.tool === 'stamp') {
    const c = stroke.points[0];
    if (!c) return Infinity;
    const r = stampHitRadius(svgW, svgH, stroke.stampScale ?? 1);
    const d = dist(p, c);
    return d <= r ? d : Infinity;
  }
  if (stroke.tool === 'callout') {
    return calloutHitsPoint(p, stroke, svgW, svgH) ? 0 : Infinity;
  }
  if (stroke.tool === 'typewriter') {
    return typewriterHitsPoint(p, stroke, svgW, svgH) ? 0 : Infinity;
  }
  if (stroke.tool === 'cloud') {
    return cloudHitsPoint(p, stroke) ? 0 : Infinity;
  }
  return distToPolyline(p.x, p.y, strokePolyline(stroke));
}

export const ERASER_HIT = 2.5;
